<template>
  <div
    ref='mapRef'
    class='b-storage_map'
  >
    <div class='map-inner'>
      <canvas id='storage_map' />
      <div class='controls'>
        <div class='zoom-in'>+</div>
        <div class='zoom-out'>-</div>
      </div>
    </div>
    <div class='legend'>
      <div class='item'>
        <div class='dot not-available' />
        <div class='label'>{{ tp('not_available_units') }}</div>
      </div>
      <div class='item'>
        <div class='dot available' />
        <div class='label'>{{ tp('available_units') }}</div>
      </div>
    </div>
    <div class='levels'>
      <div
        v-for='level in levels'
        :key='level.id'
        class='level'
        :class='{ "is-active": level.id === currentLevelId }'
        @click='selectLevel(level.id)'
      >
        {{ tp('level', { number: level.number }) }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';

import { t, prepared } from '@/helpers/i18n';

const props = defineProps({
  currentLevelId: { type: Number, required: true },
  levels: { type: Array, required: true },
  availableBoxes: { type: Array, required: true },
  filteredBoxes: { type: Array, required: true },
  fabricBoxes: { type: Array, required: true },
  hoveredBoxId: { type: Number, required: false, default: undefined }
});
const emit = defineEmits([
  'level:select',
  'box:click',
  'box:mouseover',
  'box:mouseout'
]);
const tp = prepared('frontend.storage_schema_boxes.schema');

const storageMap = ref(null);
const mapRef = ref(null);

const fabricLevelBoxes = computed(() => (
  props.fabricBoxes
    .filter(({ levelId }) => levelId === props.currentLevelId)
));

onMounted(initStorageMap);
onBeforeUnmount(destroyStorageMap);

watch(() => props.filteredBoxes, () => {
  if (storageMap.value) {
    storageMap.value.setActiveBoxIds(props.filteredBoxes.map(({ id }) => id));
  }
});

watch(() => props.currentLevelId, () => {
  destroyStorageMap();
  initStorageMap();
});

watch(() => props.hoveredBoxId, (newValue, oldValue) => {
  if (!storageMap.value) { return; }

  if (newValue) {
    storageMap.value
      .boxes
      .find(({ id }) => id === newValue)
      ?.isActive = true;
  }
  if (oldValue) {
    storageMap.value
      .boxes
      .find(({ id }) => id === oldValue)
      ?.isActive = false;
  }
});

function selectLevel(levelId) {
  if (levelId === props.currentLevelId) { return; }

  emit('level:select', levelId);
}

async function initStorageMap() {
  const { default: Map } = await import(
    /* webpackChunkName: "map" */ '@/admin/views/map/view'
  );

  storageMap.value = new Map(mapRef.value, {
    isSelectable: true,
    isResizeMapContainer: false,
    level: props.levels.find(({ id }) => id === props.currentLevelId),
    boxes: fabricLevelBoxes.value.map(boxData => ({
      ...boxData,
      state: props.availableBoxes.find(box => box.id === boxData.id) ?
        'available' :
        'not_available',
      isDisabled: !props.filteredBoxes.find(box => box.id === boxData.id)
    }))
  });

  $(mapRef.value)
    .on('box:click', (_, { boxId }) => {
      emit('box:click', { boxId });
    })
    .on('box:mouseover', (_, { boxId }) => {
      emit('box:mouseover', { boxId });
    })
    .on('box:mouseout', (_, { boxId }) => {
      emit('box:mouseout', { boxId });
    });
}

function destroyStorageMap() {
  if (storageMap.value) {
    storageMap.value.destroy();
    storageMap.value = null;
  }
}
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'

.b-storage_map
  display: flex
  flex-direction: column

  +lte_ipad
    flex-grow: 1
    height: rem4(498px)

  .map-inner
    flex-grow: 1
</style>
